import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { handleApiCall } from "../../apis";
import PermissionGate from "../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../utils/permissions";
import { endpoint } from "../../utils/endpoints";
import { notifyError, notifySuccess } from "../../utils/tostfy";
import {
  limitTextInput,
  validateWordCount,
  WORD_COUNT_HINT,
} from "../../utils/textWordValidation";
import { formatDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatBalanceRupees, formatInr } from "../../utils/formatAmount";

const formatInrDisplay = (n) => formatInr(n, { symbolSpace: false });

const REQUEST_STATUS_LABEL = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
};

const REQUEST_STATUS_CLASS = {
  Pending: "text-yellow-600",
  Approved: "text-green-600",
  Rejected: "text-red-600",
};

const REQUEST_STATUS_BG_CLASS = {
  Pending: "bg-amber-50 border-amber-200",
  Approved: "bg-green-50 border-green-200",
  Rejected: "bg-red-50 border-red-200",
};

const getStatusSectionBgClass = (statusLabel, statusCode) =>
  REQUEST_STATUS_BG_CLASS[statusLabel] ??
  REQUEST_STATUS_BG_CLASS[REQUEST_STATUS_LABEL[Number(statusCode)]] ??
  "bg-gray-50 border-gray-200";

const validateRejectReason = (value) => validateWordCount(value, "Reject reason");

const valuesDiffer = (a, b) => {
  if (a == null && b == null) return false;
  return String(a ?? "") !== String(b ?? "");
};

const resolveImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const api = process.env.REACT_APP_API || "";
  try {
    const u = new URL(api);
    const base = `${u.protocol}//${u.host}`;
    return `${base}${path.startsWith("/") ? path : `/${path}`}`;
  } catch {
    return path;
  }
};

const normalizeTripContext = (raw) => {
  const d = raw?.trip_context || raw;
  if (!d || typeof d !== "object") return null;

  const td = d.trip_details || {};
  const company = td.company_id || {};
  const companyLoc = company.company_location_id || {};
  const mipl = td.mipl_location_id || {};
  const vehicle = td.vehicle_id || {};
  const drivers = d.driver_details || {};
  const primary = drivers.primary_driver_details || {};
  const secondary = drivers.secondary_driver_details || {};
  const hasSecondary =
    secondary &&
    typeof secondary === "object" &&
    Object.keys(secondary).length > 0 &&
    (secondary.driver_name || secondary.license_number);

  const ideal = vehicle.ideal_average ?? vehicle.idealAverage;
  const idealDisplay =
    ideal != null && ideal !== "" && !Number.isNaN(Number(ideal))
      ? `${ideal} average`
      : "—";

  return {
    companyName: company.name ?? "—",
    companyLocation: companyLoc.name ?? "—",
    tripRefId: td.trip_ref_id != null ? String(td.trip_ref_id) : "—",
    dateTime: formatDateTime(td.date_time),
    startDateTime: formatDateTime(td.start_date_time),
    tripEndAt: formatDateTime(td.trip_end_at),
    miplLocation: mipl.name ?? "—",
    idealAverage: idealDisplay,
    tripAmountDetail: formatInrDisplay(td.trip_amount ?? d.trip_amount),
    challanNumber:
      td.challan_number != null && String(td.challan_number).trim() !== ""
        ? String(td.challan_number)
        : "—",
    vehicleLabel: vehicle.name ?? vehicle.registration_number ?? "—",
    primaryDriverName: primary.driver_name ?? "—",
    primaryLicense: primary.license_number ?? "—",
    secondaryDriverName: hasSecondary ? secondary.driver_name ?? "—" : "—",
    secondaryLicense: hasSecondary ? secondary.license_number ?? "—" : "—",
  };
};

const mapFuelView = (d) => {
  if (!d || typeof d !== "object") return null;
  const form = d.fuel_form || {};
  const oldV = d.old_values || {};
  const newV = d.new_values || {};
  const vehicleReg =
    form?.vehicle_id?.registration_number ||
    form?.vehicle_id?.name ||
    "—";

  const fmtNum = (n, suffix = "") => {
    if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
    return `${Number(n).toLocaleString("en-IN")}${suffix}`;
  };

  const oldOdometer = oldV.odometer_reading;
  const newOdometer = newV.odometer_reading ?? form.odometer_reading;
  const oldQty = oldV.fuel_quantity;
  const newQty = newV.fuel_quantity ?? form.fuel_quantity;
  const oldEff = oldV.old_efficiency ?? oldV.efficiency;
  const newEff = newV.efficiency ?? newV.efficency ?? form.efficency;

  return {
    requestRefId: d.request_ref_id || "—",
    requestReason: d.request_reason || "—",
    rejectReason: d.reject_reason || "",
    status: REQUEST_STATUS_LABEL[Number(d.request_status)] ?? "—",
    statusCode: d.request_status,
    receivedAt: formatDateTime(d.createdAt),
    approvedAt: formatDateTime(d.approved_at),
    rejectedAt: formatDateTime(d.rejected_at),
    fuelOperatorName: d.fuel_operator?.full_name || "—",
    fuelPumpName: d.fuel_pump?.fuel_pump_name || "—",
    fuelPumpAddress: d.fuel_pump?.address || "—",
    vehicle: vehicleReg,
    address: form.full_address || "—",
    latitude: form.latitude || "—",
    longitude: form.longitude || "—",
    old: {
      odometer: fmtNum(oldOdometer, " KM"),
      fuelQuantity: fmtNum(oldQty, " L"),
      efficiency: fmtNum(oldEff, " km/l"),
    },
    updated: {
      odometer: fmtNum(newOdometer, " KM"),
      fuelQuantity: fmtNum(newQty, " L"),
      efficiency: fmtNum(newEff, " km/l"),
      highlightOdometer: valuesDiffer(oldOdometer, newOdometer),
      highlightFuelQuantity: valuesDiffer(oldQty, newQty),
      highlightEfficiency: valuesDiffer(oldEff, newEff),
    },
    images: [
      { label: "Odometer Reading Image", src: resolveImageUrl(form.odometer_reading_image) },
      { label: "Truck Front Image", src: resolveImageUrl(form.vehicle_front_image) },
      { label: "Before Fueling Image", src: resolveImageUrl(form.before_fueling_image) },
      { label: "After Fueling Image", src: resolveImageUrl(form.after_fueling_image) },
    ].filter((img) => img.src),
  };
};

const mapAccountantView = (raw) => {
  const d = raw?.data || raw;
  if (!d || typeof d !== "object") return null;

  const req = d.request || {};
  const tripCtx = d.trip_context || {};
  const trip = normalizeTripContext(d);
  const expenseLines = Array.isArray(d.expense_change?.lines)
    ? d.expense_change.lines
    : [];
  const debitChange = d.debit_note_change;
  const waiveChange = d.waive_off_change;
  const td = tripCtx.trip_details || {};
  const debitCtx = tripCtx.debit_note_details || {};
  const primaryDriver = tripCtx.driver_details?.primary_driver_details || {};
  const isFuelWaiveOff = req.request_type === "Fuel Waive Off";

  const expenseMapped = expenseLines.map((line, index) => {
    const cur = line?.current || {};
    const reqLine = line?.requested || {};
    return {
      id: line?.trip_expense_id || `line-${index}`,
      sNo: line?.s_no ?? index + 1,
      recordedAt: formatDateTime(line?.expense_recorded_at),
      current: {
        amount: formatInrDisplay(cur.amount),
        description: cur.description || "—",
        receipt: resolveImageUrl(cur.bill_image_url),
      },
      requested: {
        amount: formatInrDisplay(reqLine.amount),
        description: reqLine.description || "—",
        receipt: resolveImageUrl(reqLine.bill_image_url),
        highlightAmount: valuesDiffer(cur.amount, reqLine.amount),
        highlightDescription: valuesDiffer(cur.description, reqLine.description),
        highlightReceipt: valuesDiffer(cur.bill_image_url, reqLine.bill_image_url),
      },
    };
  });

  const isDebitNote =
    /debit\s*note/i.test(String(req.request_type || "")) ||
    (Boolean(debitChange) && expenseMapped.length === 0 && !waiveChange);
  const isExpenseRequest =
    /expense/i.test(String(req.request_type || "")) ||
    (expenseMapped.length > 0 && !debitChange && !waiveChange);

  const debitCurrentAmount = debitCtx.amount ?? debitCtx.debit_note_amount;
  const debitCurrentDesc = debitCtx.description ?? debitCtx.remark ?? "";

  return {
    requestRefId: req.request_ref_id || "—",
    requestType: req.request_type || "—",
    requestReason: req.request_reason || "—",
    rejectReason: req.reject_reason || "",
    status: REQUEST_STATUS_LABEL[Number(req.status)] ?? "—",
    statusCode: req.status,
    submittedBy: req.submitted_by?.full_name || "—",
    submittedRole: req.submitted_by?.role || "",
    receivedAt: formatDateTime(req.submitted_at),
    approvedAt: Number(req.status) === 1 ? formatDateTime(req.updated_at) : "",
    rejectedAt: Number(req.status) === 2 ? formatDateTime(req.updated_at) : "",
    isFuelWaiveOff,
    isDebitNote,
    isExpenseRequest,
    trip,
    tripName: td.route_id ? String(td.trip_ref_id || "—") : trip?.tripRefId ?? "—",
    expenseLines: expenseMapped,
    hasExpenseChange: expenseMapped.length > 0,
    debitChange: debitChange
      ? {
          current: {
            amount: formatInrDisplay(debitCurrentAmount),
            description: debitCurrentDesc || "—",
          },
          requested: {
            amount: formatInrDisplay(debitChange.amount),
            description: debitChange.description || "—",
            dateTime: formatDateTime(debitChange.line_updated_at || debitChange.line_submitted_at),
            highlightAmount: valuesDiffer(debitCurrentAmount, debitChange.amount),
            highlightDescription: valuesDiffer(debitCurrentDesc, debitChange.description),
          },
        }
      : null,
    debitNote: debitChange
      ? {
          tripRefId: td.trip_ref_id != null ? String(td.trip_ref_id) : trip?.tripRefId ?? "—",
          amount: formatBalanceRupees(debitChange.amount),
          description: debitChange.description || "—",
        }
      : null,
    hasDebitChange: Boolean(debitChange),
    waiveChange: waiveChange
      ? {
          tripRefId: td.trip_ref_id != null ? String(td.trip_ref_id) : "—",
          driverName: primaryDriver.driver_name ?? trip?.primaryDriverName ?? "—",
          currentAwardBalance: formatBalanceRupees(tripCtx.award_amount),
          waiveDieselAmount: formatBalanceRupees(waiveChange.award_amount),
          waiveFuelLiters:
            waiveChange.waive_fuel != null && waiveChange.waive_fuel !== ""
              ? Number(waiveChange.waive_fuel)
              : null,
          balanceAwardAmount: formatBalanceRupees(waiveChange.award_balance_amount),
        }
      : null,
    hasWaiveChange: Boolean(waiveChange),
  };
};

const FIELD_HIGHLIGHT_CLASS = {
  Pending: { label: "text-amber-600", value: "text-amber-700 font-medium" },
  Approved: { label: "text-green-600", value: "text-green-700 font-medium" },
  Rejected: { label: "text-red-600", value: "text-red-700 font-medium" },
};

const Field = ({ label, value, highlight, highlightTone = "Approved" }) => {
  const tone = highlight
    ? FIELD_HIGHLIGHT_CLASS[highlightTone] ?? FIELD_HIGHLIGHT_CLASS.Approved
    : null;

  return (
    <div className="flex justify-between py-1 gap-4">
      <span className={`shrink-0 ${tone ? tone.label : "text-gray-500"}`}>{label}</span>
      <span
        className={`font-UbuntuMedium text-right break-all ${
          tone ? tone.value : "text-gray-800 font-medium"
        }`}
      >
        {value ?? "—"}
      </span>
    </div>
  );
};

const Card = ({ title, children, className = "" }) => (
  <div className={`border rounded-lg md:p-4 p-2 max-[767px]:text-sm bg-white ${className}`}>
    {title ? <h3 className="font-semibold mb-2 text-gray-700">{title}</h3> : null}
    {children}
  </div>
);

const REQUEST_DETAILS_CONTAINER_CLASS = {
  Pending: "bg-white border-gray-200",
  Approved: "bg-green-50 border-green-200",
  Rejected: "bg-red-50 border-red-200",
};

const REQUEST_DETAILS_INNER_CARD_CLASS = {
  Pending: "bg-gray-200",
  Approved: "bg-[#EBEFE8]",
  Rejected: "bg-red-100",
};

const DebitNoteStatCard = ({ value, label, cardClassName }) => (
  <div
    className={`rounded-lg md:!px-4 !px-3 md:!py-5 !py-3 md:text-center md:min-h-[100px] flex flex-col justify-center ${cardClassName}`}
  >
    <div className="text-lg md:text-xl font-UbuntuMedium text-gray-900 leading-tight break-words">
      {value ?? "—"}
    </div>
    <div className="mt-2 text-xs md:text-sm text-gray-500 font-UbuntuRegular leading-snug">
      {label}
    </div>
  </div>
);

const DebitNoteDetailsBlock = ({
  detail,
  isPending,
  actionLoading,
  onApprove,
  onReject,
}) => {
  const debit = detail.debitNote;
  if (!debit) return null;

  const status = detail.status;
  const containerClass =
    REQUEST_DETAILS_CONTAINER_CLASS[status] ?? REQUEST_DETAILS_CONTAINER_CLASS.Pending;
  const innerCardClass =
    REQUEST_DETAILS_INNER_CARD_CLASS[status] ?? REQUEST_DETAILS_INNER_CARD_CLASS.Pending;

  return (
    <>
      {detail.rejectReason ? (
        <div className="text-center mb-5">
          <p className="font-UbuntuMedium text-red-600 mb-1">Reject Reason</p>
          <p className="text-red-500 text-sm mb-0 whitespace-pre-wrap break-words">
            {detail.rejectReason}
          </p>
        </div>
      ) : null}
      <div className={`border rounded-lg p-3 md:p-5 ${containerClass}`}>
        <h3 className="font-semibold md:mb-4 !mb-3 text-gray-800 max-[767px]:text-sm">Debit Note Details</h3>
        <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-lg md:!mb-4 !mb-2">
          <DebitNoteStatCard label="Trip ID" value={debit.tripRefId} cardClassName={innerCardClass} />
          <DebitNoteStatCard label="Amount" value={debit.amount} cardClassName={innerCardClass} />
        </div>
        <div className={`rounded-lg md:!px-4 !px-3 md:!py-4 !py-2 ${innerCardClass}`}>
          <p className="text-sm text-gray-500 mb-2">Description</p>
          <p className="text-gray-800 mb-0 whitespace-pre-wrap break-words">{debit.description}</p>
        </div>
        {isPending && (
          <PermissionGate module={MODULES.MY_REQUEST} action="edit">
            <div className="flex flex-wrap justify-end gap-2.5 md:!mt-4 !mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onApprove}
                disabled={actionLoading}
              >
                Approve
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={onReject}
                disabled={actionLoading}
              >
                Reject
              </button>
            </div>
          </PermissionGate>
        )}
      </div>
    </>
  );
};

const DebitNoteChangeBlock = ({ debitChange, status, rejectReason }) => {
  if (!debitChange) return null;

  const highlightTone = status;
  const showRequestedHighlight = status === "Pending" || status === "Approved" || status === "Rejected";

  return (
    <div className="mb-4 last:mb-0">
      <h3 className="font-semibold mb-2 text-gray-600">Debit Note Change</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="Current">
          <Field label="Amount" value={debitChange.current.amount} />
          <Field label="Description" value={debitChange.current.description} />
        </Card>
        <Card title="Requested">
          <Field
            label="Amount"
            value={debitChange.requested.amount}
            highlight={showRequestedHighlight || debitChange.requested.highlightAmount}
            highlightTone={highlightTone}
          />
          <Field
            label="Description"
            value={debitChange.requested.description}
            highlight={showRequestedHighlight || debitChange.requested.highlightDescription}
            highlightTone={highlightTone}
          />
          <Field label="Date & Time" value={debitChange.requested.dateTime} />
        </Card>
      </div>
      {rejectReason ? (
        <p className="text-red-600 text-sm mt-3 mb-0">Reject reason: {rejectReason}</p>
      ) : null}
    </div>
  );
};

const WaiveOffStatCard = ({ value, label, cardClassName = "bg-gray-200" }) => (
  <div
    className={`rounded-lg px-3 md:!py-5 !py-3 md:text-center md:min-h-[100px] flex flex-col justify-center ${cardClassName}`}
  >
    <div className="text-lg md:text-xl font-UbuntuMedium text-gray-900 leading-tight break-words">
      {value ?? "—"}
    </div>
    <div className="md:!mt-2 !mt-1 text-xs md:text-sm text-gray-500 font-UbuntuRegular leading-snug">
      {label}
    </div>
  </div>
);

const FuelWaiveOffDetailsBlock = ({
  detail,
  isPending,
  actionLoading,
  onApprove,
  onReject,
}) => {
  const waive = detail.waiveChange;
  if (!waive) return null;

  const status = detail.status;
  const containerClass =
    REQUEST_DETAILS_CONTAINER_CLASS[status] ?? REQUEST_DETAILS_CONTAINER_CLASS.Pending;
  const innerCardClass =
    REQUEST_DETAILS_INNER_CARD_CLASS[status] ?? REQUEST_DETAILS_INNER_CARD_CLASS.Pending;

  const waiveDieselLabel =
    waive.waiveFuelLiters != null && !Number.isNaN(Number(waive.waiveFuelLiters))
      ? `Waive Of Diesel (${Number(waive.waiveFuelLiters)} Ltr)`
      : "Waive Of Diesel";

  return (
    <>
      {detail.rejectReason ? (
        <div className="text-center mb-5">
          <p className="font-UbuntuMedium text-red-600 mb-1">Reject Reason</p>
          <p className="text-red-500 text-sm mb-0 whitespace-pre-wrap break-words">
            {detail.rejectReason}
          </p>
        </div>
      ) : null}
      <div className={`border rounded-lg p-3 md:p-5 ${containerClass}`}>
        <h3 className="font-semibold md:!mb-4 !mb-3 text-gray-700 max-[767px]:text-sm">Fuel Waive Off Details</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4 md:!mb-4 !mb-2">
          <WaiveOffStatCard label="Trip ID" value={waive.tripRefId} cardClassName={innerCardClass} />
          <WaiveOffStatCard label="Driver Name" value={waive.driverName} cardClassName={innerCardClass} />
          <WaiveOffStatCard
            label="Current Award Balance"
            value={waive.currentAwardBalance}
            cardClassName={innerCardClass}
          />
          <WaiveOffStatCard label={waiveDieselLabel} value={waive.waiveDieselAmount} cardClassName={innerCardClass} />
          <WaiveOffStatCard
            label="Balance Award Amount"
            value={waive.balanceAwardAmount}
            cardClassName={innerCardClass}
          />
        </div>
        <div className={`rounded-lg md:!p-5 !px-4 !py-2 ${innerCardClass}`}>
          <p className="text-sm text-gray-500 mb-2">Reason For Waive-Off</p>
          <p className="text-gray-800 mb-0 whitespace-pre-wrap break-words">
            {detail.requestReason || "—"}
          </p>
        </div>
        {isPending && (
        <PermissionGate module={MODULES.MY_REQUEST} action="edit">
          <div className="flex flex-wrap justify-end gap-2.5 md:!mt-5 !mt-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onApprove}
              disabled={actionLoading}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={onReject}
              disabled={actionLoading}
            >
              Reject
            </button>
          </div>
        </PermissionGate>
      )}
      </div>
    </>
  );
};

const TripDetailsBlock = ({ trip, highlight }) => {
  if (!trip) return null;
  return (
    <>
      <h3 className="font-semibold mb-2 text-gray-600">Trip Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border rounded-lg p-4 mb-4 bg-white">
        <div>
          <Field label="Trip ID" value={trip.tripRefId} highlight={highlight?.tripRefId} />
          <Field label="Company" value={trip.companyName} highlight={highlight?.companyName} />
          <Field label="Company Location" value={trip.companyLocation} highlight={highlight?.companyLocation} />
        </div>
        <div>
          <Field label="MIPL Location" value={trip.miplLocation} highlight={highlight?.miplLocation} />
          <Field label="Vehicle" value={trip.vehicleLabel} highlight={highlight?.vehicleLabel} />
          <Field label="Date & Time" value={trip.dateTime} highlight={highlight?.dateTime} />
          <Field label="Challan Number" value={trip.challanNumber} highlight={highlight?.challanNumber} />
        </div>
      </div>
      <h3 className="font-semibold mb-2 text-gray-600">Driver Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="Primary Driver">
          <Field label="Driver Name" value={trip.primaryDriverName} highlight={highlight?.primaryDriverName} />
          <Field label="License Number" value={trip.primaryLicense} highlight={highlight?.primaryLicense} />
        </Card>
        <Card title="Secondary Driver">
          <Field label="Driver Name" value={trip.secondaryDriverName} highlight={highlight?.secondaryDriverName} />
          <Field label="License Number" value={trip.secondaryLicense} highlight={highlight?.secondaryLicense} />
        </Card>
      </div>
    </>
  );
};

const ExpenseTable = ({ rows, variant = "current", onOpenImage, onOpenDescription }) => (
  <div className="border rounded-lg md:p-3 bg-white">
    <div className="common-table-wrapper m-0 shadow-sm overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead>
          <tr>
            <th className="py-3 px-3 text-center font-UbuntuMedium">S.No.</th>
            <th className="py-3 px-3 text-center font-UbuntuMedium">Amount Spent</th>
            <th className="py-3 px-3 text-center font-UbuntuMedium">Date & Time</th>
            <th className="py-3 px-3 text-center font-UbuntuMedium">Receipt Image</th>
            <th className="py-3 px-3 text-center font-UbuntuMedium">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const data = row?.[variant] || {};
            const hasDescription = data.description && data.description !== "—";
            return (
              <tr key={`${variant}-${row.id}`} className="even:bg-gray-100">
                <td className="py-3 px-3 text-center text-gray-700">{String(row.sNo).padStart(2, "0")}</td>
                <td className="py-3 px-3 text-center text-gray-700">{data.amount || "—"}</td>
                <td className="py-3 px-3 text-center text-gray-700">{row.recordedAt || "—"}</td>
                <td className="py-3 px-3 text-center">
                  {data.receipt ? (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center px-2 py-1 border rounded bg-[#F4EED9] hover:opacity-90"
                      onClick={() => onOpenImage(data.receipt)}
                    >
                      <span className="text-xs text-gray-700">View</span>
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="py-3 px-3 text-center">
                  {hasDescription ? (
                    <button
                      type="button"
                      className="text-[#9a8b4f] underline bg-transparent border-0 p-0"
                      onClick={() => onOpenDescription(data.description)}
                    >
                      View
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="py-4 text-center text-gray-500">
                No expense details available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const ExpenseRequestDetailsBlock = ({
  detail,
  sectionClassName,
  isPending,
  actionLoading,
  onApprove,
  onReject,
  onOpenImage,
  onOpenDescription,
}) => {
  const currentRows = detail.expenseLines || [];
  const updatedRows = currentRows.filter(
    (line) =>
      line?.requested?.highlightAmount ||
      line?.requested?.highlightDescription ||
      line?.requested?.highlightReceipt
  );
  const rowsForUpdated = updatedRows.length > 0 ? updatedRows : currentRows;

  return (
    <>
      {detail.rejectReason ? (
        <div className="text-center mb-4">
          <p className="font-UbuntuMedium text-red-600 mb-1">Reject Reason</p>
          <p className="text-red-500 text-sm mb-0 whitespace-pre-wrap break-words">{detail.rejectReason}</p>
        </div>
      ) : null}
      <h3 className="font-semibold mb-2 text-gray-700">Previous Details</h3>
      <div className="border rounded-lg md:!p-3 !p-2 mb-4 bg-white">
        <h3 className="font-semibold mb-2 text-gray-700 max-[767px]:text-sm">Trip Details</h3>
        <div className="border rounded-lg md:!p-3 !p-2 bg-white mb-4 max-[767px]:text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
            <Field label="Company" value={detail.trip?.companyName} />
            <Field label="Trip ID" value={detail.trip?.tripRefId} />
            <Field label="Company Location" value={detail.trip?.companyLocation} />
            <Field label="Start Date & Time" value={detail.trip?.startDateTime} />
            <Field label="MIPL Location" value={detail.trip?.miplLocation} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 mt-3 pt-3 border-t border-gray-200">
            <Field label="Ideal Average" value={detail.trip?.idealAverage} />
            <Field label="Delivery Challan Number" value={detail.trip?.challanNumber} />
            <Field label="Trip Amount" value={detail.trip?.tripAmountDetail} />
            <Field label="Vehicle" value={detail.trip?.vehicleLabel} />
          </div>
        </div>
        <h3 className="font-semibold mb-2 text-gray-700 max-[767px]:text-sm mt-4">Expense Details</h3>
        <ExpenseTable
          rows={currentRows}
          variant="current"
          onOpenImage={onOpenImage}
          onOpenDescription={onOpenDescription}
        />
      </div>

      <h3 className="font-semibold mb-2 text-gray-700">Updated Expense Details</h3>
      <div className={`border rounded-lg p-3 ${sectionClassName}`}>
        <ExpenseTable
          rows={rowsForUpdated}
          variant="requested"
          onOpenImage={onOpenImage}
          onOpenDescription={onOpenDescription}
        />
        {isPending ? (
          <PermissionGate module={MODULES.MY_REQUEST} action="edit">
            <div className="flex justify-end gap-2.5 mt-3">
              <button
                type="button"
                className="btn btn-danger"
                onClick={onReject}
                disabled={actionLoading}
              >
                Reject
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onApprove}
                disabled={actionLoading}
              >
                Approve
              </button>
            </div>
          </PermissionGate>
        ) : null}
      </div>
    </>
  );
};

const ViewRequestDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = (searchParams.get("request_id") || "").trim();
  const role = (searchParams.get("role") || "Accountant").trim();
  const isFuelOperator = role === "Fuel Operator";
  const backToList = useBackToList("/my-requests");

  const [loading, setLoading] = useState(true);
  const [fuelDetail, setFuelDetail] = useState(null);
  const [accountantDetail, setAccountantDetail] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [sendEmailNotification, setSendEmailNotification] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState("");

  const fetchDetails = useCallback(async () => {
    if (!requestId) {
      setLoading(false);
      setFuelDetail(null);
      setAccountantDetail(null);
      return;
    }
    setLoading(true);
    try {
      const apiPath = isFuelOperator
        ? `${endpoint.viewFuelFormChangeRequest}/${encodeURIComponent(requestId)}`
        : `${endpoint.viewChangeRequest}/${encodeURIComponent(requestId)}`;

      const response = await handleApiCall("get", apiPath, {});

      if (response?.data?.code === 200) {
        const raw = response?.data?.data;
        if (isFuelOperator) {
          setFuelDetail(mapFuelView(raw));
          setAccountantDetail(null);
        } else {
          setAccountantDetail(mapAccountantView({ data: raw }));
          setFuelDetail(null);
        }
      } else {
        setFuelDetail(null);
        setAccountantDetail(null);
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load request details";
        notifyError(errMsg);
      }
    } catch {
      setFuelDetail(null);
      setAccountantDetail(null);
      notifyError("Failed to load request details");
    } finally {
      setLoading(false);
    }
  }, [requestId, isFuelOperator]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const isPending = useMemo(() => {
    const code = isFuelOperator
      ? fuelDetail?.statusCode
      : accountantDetail?.statusCode;
    return Number(code) === 0;
  }, [isFuelOperator, fuelDetail, accountantDetail]);

  const openApproveModal = () => {
    setSendEmailNotification(false);
    setShowApproveModal(true);
  };

  const closeApproveModal = () => {
    if (actionLoading) return;
    setShowApproveModal(false);
    setSendEmailNotification(false);
  };

  const handleApprove = async () => {
    if (!requestId || actionLoading) return;
    setActionLoading(true);
    try {
      const payload = { request_id: requestId,send_mail:sendEmailNotification ? 1 : 0 };
      const response = await handleApiCall("post", endpoint.approveChangeRequest, payload);
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "Request approved successfully");
        setShowApproveModal(false);
        setSendEmailNotification(false);
        navigate(backToList);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to approve request";
      notifyError(errMsg);
    } catch {
      notifyError("Failed to approve request");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = () => {
    setRejectReason("");
    setRejectError("");
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    if (actionLoading) return;
    setShowRejectModal(false);
    setRejectReason("");
    setRejectError("");
  };

  const handleReject = async () => {
    const validationError = validateRejectReason(rejectReason);
    if (validationError) {
      setRejectError(validationError);
      return;
    }
    const reason = rejectReason.trim();
    if (!requestId || actionLoading) return;
    setActionLoading(true);
    setRejectError("");
    try {
      const response = await handleApiCall("post", endpoint.rejectChangeRequest, {
        request_id: requestId,
        reject_reason: reason,
      });
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "Request rejected successfully");
        setShowRejectModal(false);
        setRejectReason("");
        setRejectError("");
        navigate(backToList);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to reject request";
      notifyError(errMsg);
    } catch {
      notifyError("Failed to reject request");
    } finally {
      setActionLoading(false);
    }
  };

  const headerMeta = useMemo(() => {
    if (isFuelOperator && fuelDetail) {
      return {
        received: fuelDetail.receivedAt,
        approved: fuelDetail.approvedAt,
        rejected: fuelDetail.rejectedAt,
        status: fuelDetail.status,
        refId: fuelDetail.requestRefId,
      };
    }
    if (!isFuelOperator && accountantDetail) {
      return {
        received: accountantDetail.receivedAt,
        approved: accountantDetail.approvedAt,
        rejected: accountantDetail.rejectedAt,
        status: accountantDetail.status,
        refId: accountantDetail.requestRefId,
        receivedLabel:
          accountantDetail.isDebitNote && accountantDetail.status === "Approved"
            ? "Submitted Date & Time"
            : "Received Date & Time",
      };
    }
    return null;
  }, [isFuelOperator, fuelDetail, accountantDetail]);

  const updatedDetailsSectionClass = useMemo(() => {
    const statusLabel = headerMeta?.status;
    const statusCode = isFuelOperator
      ? fuelDetail?.statusCode
      : accountantDetail?.statusCode;
    return getStatusSectionBgClass(statusLabel, statusCode);
  }, [headerMeta?.status, isFuelOperator, fuelDetail?.statusCode, accountantDetail?.statusCode]);

  if (!requestId && !loading) {
    return (
      <div className="">
        <p className="text-gray-500 mb-3">Missing request id. Open details from the list.</p>
        <Link to={backToList} className="btn btn-primary">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="md:mt-0 !mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 md:mb-4 !mb-3">
        <Link to={backToList} className="btn btn-primary">
          Back
        </Link>
        {!loading &&
          isPending &&
          !accountantDetail?.isFuelWaiveOff &&
          !accountantDetail?.isDebitNote &&
          !accountantDetail?.isExpenseRequest && (
          <PermissionGate module={MODULES.MY_REQUEST} action="edit">
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                className="btn btn-primary"
                onClick={openApproveModal}
                disabled={actionLoading}
              >
                Accept
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={openRejectModal}
                disabled={actionLoading}
              >
                Reject
              </button>
            </div>
          </PermissionGate>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">Loading...</div>
      ) : isFuelOperator && !fuelDetail ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          Could not load fuel form request details.
        </div>
      ) : !isFuelOperator && !accountantDetail ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          Could not load request details.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between md:gap-4 gap-1 md:mb-4 !mb-3">
            <h2 className="font-semibold mb-0 text-gray-700 max-[767px]:text-base">
              Details {headerMeta?.refId ? `(${headerMeta.refId})` : ""}
            </h2>
            {headerMeta && (
              <div className="flex flex-wrap justify-between text-gray-600 items-center gap-x-4 gap-y-2 text-sm">
                <div>
                  {headerMeta.receivedLabel ?? "Received Date & Time"}: <b>{headerMeta.received}</b>
                </div>
                {headerMeta.approved && headerMeta.approved !== "—" && (
                  <>
                    <span className="h-8 bg-slate-300 w-[2px] hidden sm:block" />
                    <div>
                      Approved Date & Time: <b>{headerMeta.approved}</b>
                    </div>
                  </>
                )}
                {headerMeta.rejected && headerMeta.rejected !== "—" && (
                  <>
                    <span className="h-8 bg-slate-300 w-[2px] hidden sm:block" />
                    <div>
                      Rejected Date & Time: <b>{headerMeta.rejected}</b>
                    </div>
                  </>
                )}
                <span className="h-8 bg-slate-300 w-[2px] hidden sm:block" />
                <div>
                  Status:{" "}
                  <b className={REQUEST_STATUS_CLASS[headerMeta.status] || ""}>
                    {headerMeta.status}
                  </b>
                </div>
              </div>
            )}
          </div>

          {isFuelOperator && fuelDetail && (
            <>
              <div className="md:border rounded-lg md:p-4 md:mb-4 bg-white">
                <Card>
                  <Field label="Request Reason" value={fuelDetail.requestReason} />
                  <Field label="Fuel Operator" value={fuelDetail.fuelOperatorName} />
                  <Field label="Fuel Pump" value={fuelDetail.fuelPumpName} />
                  <Field label="Fuel Pump Address" value={fuelDetail.fuelPumpAddress} />
                </Card>



                <h3 className="font-semibold mb-2 text-gray-600 mt-4">Current Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border rounded-lg md:!p-4 !p-2 mb-4 max-[767px]:text-sm">
                  <div>
                    <Field label="Vehicle" value={fuelDetail.vehicle} />
                    <Field label="Odometer Reading" value={fuelDetail.old.odometer} />
                    <Field label="Fuel Quantity" value={fuelDetail.old.fuelQuantity} />
                  </div>
                  <div>
                    <Field label="Fuel Efficiency" value={fuelDetail.old.efficiency} />
                    <Field label="Address" value={fuelDetail.address} />
                    <Field label="Latitude" value={fuelDetail.latitude} />
                    <Field label="Longitude" value={fuelDetail.longitude} />
                  </div>
                </div>
              </div>

              <h2 className="font-semibold mb-2 text-gray-700 max-[767px]:text-base">Updated Details</h2>
              <div className={`border rounded-lg md:!p-4 !p-2 ${updatedDetailsSectionClass}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border rounded-lg md:!p-4 !p-2 md:!mb-4 !mb-2 bg-white max-[767px]:text-sm">
                  <div>
                    <Field label="Vehicle" value={fuelDetail.vehicle} />
                    <Field
                      label="Odometer Reading"
                      value={fuelDetail.updated.odometer}
                      highlight={fuelDetail.updated.highlightOdometer}
                    />
                    <Field
                      label="Fuel Quantity"
                      value={fuelDetail.updated.fuelQuantity}
                      highlight={fuelDetail.updated.highlightFuelQuantity}
                    />
                  </div>
                  <div>
                    <Field
                      label="Fuel Efficiency"
                      value={fuelDetail.updated.efficiency}
                      highlight={fuelDetail.updated.highlightEfficiency}
                    />
                    <Field label="Address" value={fuelDetail.address} />
                  </div>
                </div>

                {fuelDetail.rejectReason ? (
                  <p className="text-red-600 text-sm mb-3">
                    Reject reason: {fuelDetail.rejectReason}
                  </p>
                ) : null}

                {fuelDetail.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 md:!gap-4 !gap-2">
                    {fuelDetail.images.map((item) => (
                      <div
                        key={item.label}
                        onClick={() => setSelectedImage(item.src)}
                        className="bg-gray-200 rounded-xl h-[110px] flex flex-col justify-center items-center cursor-pointer hover:bg-gray-300 transition p-2"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setSelectedImage(item.src)}
                      >
                        <img src={item.src} alt={item.label} className="h-9 mb-2 object-contain" />
                        <p className="md:text-sm text-xs text-gray-700 text-center px-2">{item.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {!isFuelOperator && accountantDetail && accountantDetail.isFuelWaiveOff && (
            <FuelWaiveOffDetailsBlock
              detail={accountantDetail}
              isPending={isPending}
              actionLoading={actionLoading}
              onApprove={openApproveModal}
              onReject={openRejectModal}
            />
          )}

          {!isFuelOperator && accountantDetail && accountantDetail.isDebitNote && (
            <DebitNoteDetailsBlock
              detail={accountantDetail}
              isPending={isPending}
              actionLoading={actionLoading}
              onApprove={openApproveModal}
              onReject={openRejectModal}
            />
          )}

          {!isFuelOperator && accountantDetail && accountantDetail.isExpenseRequest && (
            <ExpenseRequestDetailsBlock
              detail={accountantDetail}
              sectionClassName={updatedDetailsSectionClass}
              isPending={isPending}
              actionLoading={actionLoading}
              onApprove={openApproveModal}
              onReject={openRejectModal}
              onOpenImage={setSelectedImage}
              onOpenDescription={setSelectedDescription}
            />
          )}

          {!isFuelOperator &&
            accountantDetail &&
            !accountantDetail.isFuelWaiveOff &&
            !accountantDetail.isDebitNote &&
            !accountantDetail.isExpenseRequest && (
            <>
              <div className="border rounded-lg p-4 mb-4 bg-white">
                <Card>
                  <Field label="Request Type" value={accountantDetail.requestType} />
                  <Field label="Request Reason" value={accountantDetail.requestReason} />
                  <Field
                    label="Submitted By"
                    value={
                      accountantDetail.submittedRole
                        ? `${accountantDetail.submittedBy} (${accountantDetail.submittedRole})`
                        : accountantDetail.submittedBy
                    }
                  />
                </Card>
                <TripDetailsBlock trip={accountantDetail.trip} />
              </div>

              <h2 className="font-semibold mb-2 text-gray-700">Updated Details</h2>
              <div className={`border rounded-lg p-4 ${updatedDetailsSectionClass}`}>
                {accountantDetail.hasExpenseChange &&
                  accountantDetail.expenseLines.map((line) => (
                    <div key={line.id} className="mb-6 last:mb-0">
                      <h3 className="font-semibold mb-2 text-gray-600">
                        Expense #{line.sNo} — {line.recordedAt}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card title="Current">
                          <Field label="Amount" value={line.current.amount} />
                          <Field label="Description" value={line.current.description} />
                          {line.current.receipt ? (
                            <img
                              src={line.current.receipt}
                              alt="Current receipt"
                              className="mt-2 max-h-24 cursor-pointer rounded border"
                              onClick={() => setSelectedImage(line.current.receipt)}
                            />
                          ) : null}
                        </Card>
                        <Card title="Requested">
                          <Field
                            label="Amount"
                            value={line.requested.amount}
                            highlight={line.requested.highlightAmount}
                          />
                          <Field
                            label="Description"
                            value={line.requested.description}
                            highlight={line.requested.highlightDescription}
                          />
                          {line.requested.receipt ? (
                            <img
                              src={line.requested.receipt}
                              alt="Requested receipt"
                              className="mt-2 max-h-24 cursor-pointer rounded border"
                              onClick={() => setSelectedImage(line.requested.receipt)}
                            />
                          ) : null}
                        </Card>
                      </div>
                    </div>
                  ))}

                {accountantDetail.hasDebitChange && (
                  <DebitNoteChangeBlock
                    debitChange={accountantDetail.debitChange}
                    status={accountantDetail.status}
                    rejectReason={accountantDetail.rejectReason}
                  />
                )}

                {accountantDetail.hasWaiveChange && accountantDetail.waiveChange && (
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-600">Waive Off Change</h3>
                    <Card>
                      <Field label="Trip ID" value={accountantDetail.waiveChange.tripRefId} />
                      <Field label="Driver Name" value={accountantDetail.waiveChange.driverName} />
                      <Field
                        label="Current Award Balance"
                        value={accountantDetail.waiveChange.currentAwardBalance}
                      />
                      <Field
                        label={
                          accountantDetail.waiveChange.waiveFuelLiters != null
                            ? `Waive Of Diesel (${accountantDetail.waiveChange.waiveFuelLiters} Ltr)`
                            : "Waive Of Diesel"
                        }
                        value={accountantDetail.waiveChange.waiveDieselAmount}
                      />
                      <Field
                        label="Balance Award Amount"
                        value={accountantDetail.waiveChange.balanceAwardAmount}
                      />
                    </Card>
                  </div>
                )}

                {!accountantDetail.hasExpenseChange &&
                  !accountantDetail.hasDebitChange &&
                  !accountantDetail.hasWaiveChange && (
                    <p className="text-gray-500 text-sm mb-0">No change details available.</p>
                  )}

                {accountantDetail.rejectReason && !accountantDetail.hasDebitChange ? (
                  <p className="text-red-600 text-sm mt-3 mb-0">
                    Reject reason: {accountantDetail.rejectReason}
                  </p>
                ) : null}
              </div>
            </>
          )}
        </>
      )}

      <Modal
        dialogClassName="modal-approve-request"
        show={showApproveModal}
        onHide={closeApproveModal}
        centered
        className="font-UbuntuRegular"
      >
        <Modal.Body className="p-0">
          <div className="card logout-card common_box border-0 bg-white shadow-sm">
            <div className="card-body common_box_body !py-10 !px-8 font-UbuntuRegular">
              <div className="flex flex-col items-center text-center w-full">
                <h3 className="font-UbuntuMedium text-xl text-[#000053] mb-3 w-full">
                  Approve Request?
                </h3>
                <p className="text-gray-600 text-[15px] leading-relaxed mb-6 w-full max-w-[280px] mx-auto">
                  Are you sure you want to approve this request
                </p>
                <label className="flex items-center justify-center gap-2.5 mb-8 cursor-pointer text-gray-700 text-sm w-full">
                  <input
                    type="checkbox"
                    className="w-4 h-4 shrink-0 rounded border-gray-300 accent-secondary"
                    checked={sendEmailNotification}
                    onChange={(e) => setSendEmailNotification(e.target.checked)}
                    disabled={actionLoading}
                  />
                  <span className="text-left leading-snug">
                    Send email notification to requestor
                  </span>
                </label>
                <div className="flex items-center justify-center gap-3 w-full max-w-[340px]">
                  <button
                    type="button"
                    className="flex-1 py-2.5 px-4 rounded-[5px] border-2 border-[#030253] text-secondary bg-white font-UbuntuMedium hover:bg-secondary hover:text-white transition-colors disabled:opacity-60 text-[13px]"
                    onClick={closeApproveModal}
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="flex-1 py-2.5 px-4 rounded-[5px] border-2 border-[#000053] bg-[#000053] text-white font-UbuntuMedium  hover:text-primary transition-colors disabled:opacity-60 text-[13px]"
                    onClick={handleApprove}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Confirming..." : "Confirm Approval"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        dialogClassName="modal-approve-request"
        show={showRejectModal}
        onHide={closeRejectModal}
        centered
        className="font-UbuntuRegular"                                 
      >
        <Modal.Body className="p-0">
          <div className="card logout-card common_box border-0 bg-white shadow-sm">
            <div className="card-body common_box_body !py-8 !px-8 font-UbuntuRegular">
              <h3 className="font-UbuntuMedium text-xl text-[#000053] mb-1">Reject Request</h3>
              <p className="text-gray-500 text-sm mb-5">
                Please provide a reason for rejecting this request.
              </p>
              <label className="block text-sm text-gray-700 mb-2 font-UbuntuMedium">
                Reject reason <span className="text-danger">*</span>
              </label>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-800 font-UbuntuRegular text-sm resize-none focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:bg-gray-100"
                rows={4}
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(limitTextInput(e.target.value));
                  if (rejectError) setRejectError("");
                }}
                placeholder="Enter reason for rejection"
                disabled={actionLoading}
              />
              <p className="text-xs text-gray-500 mt-1 mb-0">{WORD_COUNT_HINT}</p>
              {rejectError ? (
                <p className="text-danger text-sm mt-2 mb-0">{rejectError}</p>
              ) : null}
              <div className="flex items-center justify-center gap-3 w-full mt-6">
                <button
                  type="button"
                  className="flex-1 py-2.5 px-4 rounded-[5px] border-2 border-[#030253] bg-white text-[#000053] font-UbuntuMedium hover:bg-primary hover:text-[#000053] transition-colors disabled:opacity-60 text-[13px]"
                  onClick={closeRejectModal}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 py-2.5 px-4 rounded-[5px] border-2 border-danger bg-danger text-white font-UbuntuMedium hover:opacity-90 transition-opacity disabled:opacity-60 text-[13px]"
                  onClick={handleReject}
                  disabled={actionLoading}
                >
                  {actionLoading ? "Rejecting..." : "Confirm Reject"}
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {selectedDescription && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-[9999] p-4">
          <div className="relative bg-white p-4 rounded-lg max-w-xl w-full">
            <button
              onClick={() => setSelectedDescription("")}
              className="absolute top-2 right-3 text-black text-xl font-bold"
              type="button"
            >
              x
            </button>
            <h4 className="font-UbuntuMedium mb-3 text-gray-800">Description</h4>
            <p className="mb-0 text-gray-700 whitespace-pre-wrap break-words">{selectedDescription}</p>
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-[9999] p-4">
          <div className="relative bg-white p-4 rounded-lg max-w-2xl w-full">
            <button
              onClick={() => setSelectedImage("")}
              className="absolute top-2 right-3 text-black text-xl font-bold"
              type="button"
            >
              x
            </button>
            <img
              src={selectedImage}
              alt="Preview"
              className="w-full h-auto rounded max-h-[75vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewRequestDetails;
